import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { NAV } from "@/constants/testIds";

const SECTIONS = [
  { id: "platform", href: "/platform", label: "Platform", testId: NAV.linkPlatform },
  { id: "technology", href: "/technology", label: "Technology", testId: NAV.linkTechnology },
  { id: "ai", href: "/ai", label: "AI", testId: NAV.linkAI },
  { id: "applications", href: "/applications", label: "Applications", testId: NAV.linkApplications },
  { id: "team", href: "/team", label: "Team", testId: NAV.linkTeam },
];

export function Navbar({ onOpenContact }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#020617]/70 border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 h-[72px] flex items-center justify-between">
        <Link
          to="/"
          data-testid={NAV.logo}
          className="flex items-center gap-3 group"
          onClick={() => setOpen(false)}
        >
          <span className="inline-flex items-center">
            <img
              src="/assets/logo/neurobolt_icon_clean.svg"
              alt=""
              className="h-9 w-9 transition-transform duration-700 group-hover:rotate-6"
            />
          </span>
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-[19px] font-semibold tracking-tight text-white">
              Neurobolt<span className="text-[#38BDF8]">.AI</span>
            </span>
            <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#94A3B8] mt-1">
              Rewire · Refine · Redefine
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {SECTIONS.map((s) => (
            <Link
              key={s.id}
              data-testid={s.testId}
              to={s.href}
              className={`nav-link text-[13px] tracking-wide text-[#CBD5E1] hover:text-white transition-colors ${
                loc.pathname === s.href ? "active text-white" : ""
              }`}
            >
              {s.label}
            </Link>
          ))}
          <Link
            data-testid={NAV.linkResearch}
            to="/research"
            className={`nav-link text-[13px] tracking-wide text-[#CBD5E1] hover:text-white transition-colors ${
              loc.pathname === "/research" ? "active text-white" : ""
            }`}
          >
            Research
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid={NAV.ctaContact}
            onClick={onOpenContact}
            className="hidden md:inline-flex btn-glow items-center gap-2 px-5 py-2.5 rounded-full bg-white text-[#020617] text-[13px] font-semibold tracking-wide hover:bg-[#BFDBFE] transition-colors"
          >
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#0F172A]" />
            Partner with us
          </button>
          <button
            data-testid={NAV.mobileToggle}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/10 text-white"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#020617]/95 backdrop-blur-xl border-t border-white/5">
          <div className="px-6 py-5 flex flex-col gap-4">
            {SECTIONS.map((s) => (
              <Link
                key={s.id}
                to={s.href}
                onClick={() => setOpen(false)}
                className="text-left text-[15px] text-[#CBD5E1] hover:text-white"
              >
                {s.label}
              </Link>
            ))}
            <Link
              to="/research"
              onClick={() => setOpen(false)}
              className="text-left text-[15px] text-[#CBD5E1] hover:text-white"
            >
              Research
            </Link>
            <button
              onClick={() => {
                setOpen(false);
                onOpenContact();
              }}
              className="mt-2 inline-flex items-center justify-center px-5 py-3 rounded-full bg-white text-[#020617] text-[14px] font-semibold"
            >
              Partner with us
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
